import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ncoder/ui';
import {
  EnvironmentsList,
  VersionManagement,
  TrafficManagement,
  DeploymentMonitoring,
  DeploymentStatusPanel,
  QuickActionsPanel,
  HealthCheckPanel,
} from '../../molecules/deployment';

interface Deployment {
  id: string;
  name: string;
  environment: string;
  status: 'active' | 'inactive' | 'deploying';
  version: string;
  endpoint: string;
  traffic: string;
  lastDeployed: string;
  uptime: string;
  requestsPerMin: string;
}

interface Version {
  version: string;
  description: string;
  isCurrent: boolean;
}

interface Metrics {
  uptime: string;
  requests: string;
  responseTime: string;
  errors: string;
}

interface HealthCheck {
  service: string;
  status: 'healthy' | 'warning' | 'critical';
  responseTime: string;
  lastChecked: string;
}

interface AgentDeploymentContentProps {
  deployments: Deployment[];
  versions: Version[];
  metrics: Metrics;
  healthChecks: HealthCheck[];
  productionTraffic: string;
  canaryEnabled: boolean;
  activeDeployments: number;
  totalEnvironments: number;
  currentVersion: string;
  onSettings: (id: string) => void;
  onViewLogs: (id: string) => void;
  onRollback: (version: string) => void;
  onDeploy: (version: string) => void;
  onProductionTrafficChange: (value: string) => void;
  onCanaryEnabledChange: (enabled: boolean) => void;
  onQuickDeploy: () => void;
  onConfiguration: () => void;
  onViewLiveAgent: () => void;
}

export const AgentDeploymentContent: React.FC<AgentDeploymentContentProps> = ({
  deployments,
  versions,
  metrics,
  healthChecks,
  productionTraffic,
  canaryEnabled,
  activeDeployments,
  totalEnvironments,
  currentVersion,
  onSettings,
  onViewLogs,
  onRollback,
  onDeploy,
  onProductionTrafficChange,
  onCanaryEnabledChange,
  onQuickDeploy,
  onConfiguration,
  onViewLiveAgent,
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content - 2/3 width */}
      <div className="lg:col-span-2 space-y-6">
        <Tabs defaultValue="environments" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="environments">Environments</TabsTrigger>
            <TabsTrigger value="versions">Versions</TabsTrigger>
            <TabsTrigger value="traffic">Traffic</TabsTrigger>
            <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
          </TabsList>

          <TabsContent value="environments" className="space-y-4 mt-4">
            <EnvironmentsList
              deployments={deployments}
              onSettings={onSettings}
              onViewLogs={onViewLogs}
            />
          </TabsContent>

          <TabsContent value="versions" className="space-y-4 mt-4">
            <VersionManagement
              versions={versions}
              onRollback={onRollback}
              onDeploy={onDeploy}
            />
          </TabsContent>

          <TabsContent value="traffic" className="space-y-4 mt-4">
            <TrafficManagement
              productionTraffic={productionTraffic}
              canaryEnabled={canaryEnabled}
              onProductionTrafficChange={onProductionTrafficChange}
              onCanaryEnabledChange={onCanaryEnabledChange}
            />
          </TabsContent>

          <TabsContent value="monitoring" className="space-y-4 mt-4">
            <DeploymentMonitoring metrics={metrics} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Sidebar - 1/3 width */}
      <div className="space-y-6">
        <DeploymentStatusPanel
          activeDeployments={activeDeployments}
          totalEnvironments={totalEnvironments}
          currentVersion={currentVersion}
        />
        <QuickActionsPanel
          onQuickDeploy={onQuickDeploy}
          onConfiguration={onConfiguration}
          onViewLiveAgent={onViewLiveAgent}
        />
        <HealthCheckPanel healthChecks={healthChecks} />
      </div>
    </div>
  );
};
